import React, { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useSelector } from "react-redux";
import format from "date-fns/esm/format";
import parseISO from "date-fns/esm/parseISO";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
// Removed DatePicker imports
import { typoThin, typoMedium, typoBase, smallBold, xlBold, thinBold  } from "../styles/typography";

// Static time unit translations - defined outside component to prevent re-renders
const TIME_UNIT_TRANSLATIONS = {
  hours: {
    en: 'h',
    nl: 'u'  // Dutch abbreviation for "uur" (hours)
  },
  minutes: {
    en: 'm',
    nl: 'm'  // Dutch abbreviation for "minuten" (minutes) - keeping 'm' as it's universal
  }
};

const TimesheetCard = () => {
  const { t, i18n } = useTranslation('common');
  const { timesheets, loading } = useSelector((state) => state.timesheetList);
  const { user } = useSelector((state) => state.userLoaded); // Get current user

  // Filter timesheets by current user (similar to TimesheetsPage.js logic) - MEMOIZED
  const filteredTimesheets = useMemo(() => {
    return (timesheets || []).filter((timesheet) => {
      // If user is not a manager, they can only see their own timesheets
      if (user && user.role !== "Manager" && timesheet.owner?._id !== user.relationship) {
        return false;
      }
      return true;
    });
  }, [timesheets, user]);

  // Safe time unit translation function - uses static translations
  const getTimeUnit = (unit) => {
    const currentLang = i18n.language || 'en';
    const unitTranslation = TIME_UNIT_TRANSLATIONS[unit];
    if (unitTranslation && unitTranslation[currentLang]) {
      return unitTranslation[currentLang];
    }
    return unit === 'hours' ? 'h' : 'm'; // fallback
  };
  const [selectedDate, setSelectedDate] = useState(
    format(new Date(), "yyyy-MM-dd")
  );

  const changeDateByDays = (days) => {
    const currentDate = parseISO(selectedDate);
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(format(newDate, "yyyy-MM-dd"));
  };

  const handleDateSelect = (event) => {
    const inputValue = event.target.value; // 'DD-MM-YYYY'
    
    // Convert DD-MM-YYYY to yyyy-MM-dd
    const parts = inputValue.split('-');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      const isoDate = `${year}-${month}-${day}`;
      setSelectedDate(isoDate);
    }
  };

  // Format date for display as DD-MM-YYYY
  const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}-${month}-${year}`;
  };

  const convertToSeconds = (time) => {
    if (!time || typeof time !== 'string') return 0;
    
    try {
      const [hours, minutes, seconds] = time
        .split(":")
        .map((part) => part.trim().split(" ")[0])
        .map(Number);
      
      if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return 0;
      
      return hours * 3600 + minutes * 60 + seconds;
    } catch (error) {
      return 0;
    }
  };


  const formatHoursAndMinutes = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    const hoursUnit = getTimeUnit('hours');
    const minutesUnit = getTimeUnit('minutes');
    
    if (hours === 0 && minutes === 0) {
      return `0${hoursUnit} 0${minutesUnit}`;
    } else if (hours === 0) {
      return `${minutes}${minutesUnit}`;
    } else if (minutes === 0) {
      return `${hours}${hoursUnit}`;
    } else {
      return `${hours}${hoursUnit} ${minutes}${minutesUnit}`;
    }
  };

  // Memoize heavy calculations to prevent re-computation on every render
  const { totalHoursWorkedInSeconds, billableSeconds, nonBillableSeconds, totalSecondsInDay } = useMemo(() => {
    const totalHours = (filteredTimesheets || [])
      .filter(
        (timesheet) =>
          timesheet?.date && format(new Date(timesheet.date), "yyyy-MM-dd") === selectedDate
      )
      .reduce(
        (acc, timesheet) => acc + convertToSeconds(timesheet?.totalDuration),
        0
      );

    const billable = (filteredTimesheets || [])
      .filter(
        (timesheet) =>
          timesheet?.date && 
          format(new Date(timesheet.date), "yyyy-MM-dd") === selectedDate &&
          timesheet.isBilled === true
      )
      .reduce(
        (acc, timesheet) => acc + convertToSeconds(timesheet?.totalDuration),
        0
      );

    return {
      totalHoursWorkedInSeconds: totalHours,
      billableSeconds: billable,
      nonBillableSeconds: totalHours - billable,
      totalSecondsInDay: 24 * 3600
    };
  }, [filteredTimesheets, selectedDate]);

  const totalBillableLastWeekInSeconds = useMemo(() => {
    return (filteredTimesheets || [])
      .filter((timesheet) => {
        if (!timesheet?.date || !timesheet.isBilled) return false;
        
        try {
          const timesheetDateStr = format(new Date(timesheet.date), "yyyy-MM-dd");
          const selectedDateStr = selectedDate;
          const selectedDateObj = parseISO(selectedDate);
          const sixDaysAgo = new Date(selectedDateObj);
          sixDaysAgo.setDate(selectedDateObj.getDate() - 6);
          const sixDaysAgoStr = format(sixDaysAgo, "yyyy-MM-dd");
          
          return (
            timesheetDateStr >= sixDaysAgoStr && 
            timesheetDateStr <= selectedDateStr
          );
        } catch (error) {
          return false;
        }
      })
      .reduce(
        (acc, timesheet) => acc + convertToSeconds(timesheet?.totalDuration),
        0
      );
  }, [filteredTimesheets, selectedDate]);

  const totalBillableLastMonthInSeconds = useMemo(() => {
    return (filteredTimesheets || [])
      .filter((timesheet) => {
        if (!timesheet?.date || !timesheet.isBilled) return false;
        
        try {
          const timesheetDateStr = format(new Date(timesheet.date), "yyyy-MM-dd");
          const selectedDateStr = selectedDate;
          const selectedDateObj = parseISO(selectedDate);
          const twentyNineDaysAgo = new Date(selectedDateObj);
          twentyNineDaysAgo.setDate(selectedDateObj.getDate() - 29);
          const twentyNineDaysAgoStr = format(twentyNineDaysAgo, "yyyy-MM-dd");
          
          return (
            timesheetDateStr >= twentyNineDaysAgoStr && 
            timesheetDateStr <= selectedDateStr
          );
        } catch (error) {
          return false;
        }
      })
      .reduce(
        (acc, timesheet) => acc + convertToSeconds(timesheet?.totalDuration),
        0
      );
  }, [filteredTimesheets, selectedDate]);

  // Show loading spinner only when there's no data at all
  // If data exists but we're refetching (loading=true), show the data instead
  if (!timesheets || (timesheets.length === 0 && loading)) {
    return (
      <Card sx={{ boxShadow: "none", border: "none" }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{  boxShadow: "none", border: "none" }}>
      <CardContent>
        {/* Date Navigation */}
        <Box
          display="flex"
          justifyContent="center"
          gap="10px"
          alignItems="center"
        >
          <Box>
            <IconButton
              onClick={() => changeDateByDays(-1)}
              sx={{ padding: "5px" }}
            >
              <ArrowBack sx={{ fontSize: "16px", color: "#2B80B6" }} />
            </IconButton>
            <IconButton
              onClick={() => changeDateByDays(1)}
              sx={{ padding: "5px" }}
            >
              <ArrowForward sx={{ fontSize: "16px", color: "#2B80B6" }} />
            </IconButton>
          </Box>

          <Box display="flex" justifyContent="center" alignItems="center">
            <TextField
              type="text"
              label={t('common:date')}
              name="date"
              value={formatDateForDisplay(selectedDate)}
              onChange={handleDateSelect}
              placeholder="DD-MM-YYYY"
              size="small"
              variant="outlined" // Ensure you're using the 'outlined' variant to target its classes
              sx={{
                width: 105,
                "& .MuiInputBase-input": {...typoMedium, padding: 0}
                ,
                "& .MuiInputLabel-root": {
                  display: "none", // Hide the label if not needed
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none", // Remove the default border
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none", // Remove border on hover
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none", // Remove border when focused
                },
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        {/* Circular Progress Bar */}
        <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
          <Box width="200px" height="200px" position="relative">
            {/* Base Circular Progressbar - Total Seconds in Day */}
            <Box
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="100%"
            >
              <CircularProgressbar
                value={totalSecondsInDay}
                maxValue={totalSecondsInDay}
                styles={buildStyles({
                  pathColor: "#ECECEC",
                  trailColor: "transparent",
                  strokeLinecap: "butt",
                })}
              />
            </Box>

            {/* Non-Billable + Billable Seconds */}
            <Box
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="100%"
            >
              <CircularProgressbar
                value={nonBillableSeconds + billableSeconds}
                maxValue={totalSecondsInDay}
                styles={buildStyles({
                  pathColor: "#e34b4bff",
                  trailColor: "transparent",
                  strokeLinecap: "butt",
                })}
              />
            </Box>

            {/* Billable Seconds */}
            <Box
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="100%"
            >
              <CircularProgressbar
                value={billableSeconds}
                maxValue={totalSecondsInDay}
                styles={buildStyles({
                  pathColor: "#84BC14",
                  trailColor: "transparent",
                  strokeLinecap: "butt",
                })}
              />
            </Box>

            {/* Worked Time Overlay */}
            <Box
              position="absolute"
              top="50%"
              left="50%"
              sx={{
                transform: "translate(-50%, -50%)",
                textAlign: "center",
              }}
            >
              <Typography variant="h6" sx={xlBold}>
                {formatHoursAndMinutes(totalHoursWorkedInSeconds)}
              </Typography>
              <Typography variant="caption" sx={typoThin}>{t('common:worked')}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Textual Info */}
        <Box style={{ marginTop: "20px", width: "100%",  }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box display="flex" alignItems="center" >
              <span
                style={{
                  display: "inline-block",
                  width: "7px",
                  height: "7px",
                  backgroundColor: "#84BC14",
                  borderRadius: "50%",
                  marginRight: "8px",
                }}
              ></span>
              <Typography
                variant="body1"
                style={{ ...typoBase, flexGrow: 1, textAlign: "left" }}
              >
                {t('common:billed_hours')}:
              </Typography>
            </Box>
            <Typography
              variant="body1"
              style={{ ...thinBold, textAlign: "right"}}
            >
              {formatHoursAndMinutes(billableSeconds)}
            </Typography>
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box display="flex" alignItems="center">
              <span
                style={{
                  display: "inline-block",
                  width: "7px",
                  height: "7px",
                  backgroundColor: "#e34b4bff",
                  borderRadius: "50%",
                  marginRight: "8px",
                }}
              ></span>
              <Typography
                variant="body1"
                style={{ ...typoBase, flexGrow: 1, textAlign: "left" }}
              >
                {t('common:not_billed_hours')}:
              </Typography>
            </Box>
            <Typography
              variant="body1"
              style={{ ...thinBold, textAlign: "right"}}
            >
              {formatHoursAndMinutes(nonBillableSeconds)}
            </Typography>
          </Box>

        </Box>

        {/* Weekly and Monthly Billed Hours */}
        <Box
          style={{
            
            marginTop: "25px",
            width: "100%",
          }}
        >
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2">
              <span style={smallBold}>{t('common:labels.weekBilledHours')}:</span>{" "}
              <span style={typoBase}>{formatHoursAndMinutes(totalBillableLastWeekInSeconds)}</span>
            </Typography>
            <Typography variant="body2">
              <span style={smallBold}>{t('common:labels.monthBilledHours')}:</span>{" "}
              <span style={typoBase}>{formatHoursAndMinutes(totalBillableLastMonthInSeconds)}</span>
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TimesheetCard;
